/** You can throw these errors in your app and the ErrorBoundary will catch them and display an appropriate error
 *  screen. Beware that this only works for the render function and _not_ for
 *
 *  - event handlers, like `onClick`
 *  - asynchronous code, like `setTimeout` or any `async` function
 *
 *  for these scenarios, you either have to set some error state and throw in the render function based on that state,
 *  or if you are using a `@tanstack/react-query` for data-fetching, you can enable the `throwOnError: true` option
 *  in your query and either patch the `queryFn` to throw a `RenderError...` or, if you are fetching h2o API, either
 *  gRPC Gateway or Twirp, the ErrorBoundary will recognize the error response and will use appropriate error screen
 *  automatically.
 **/

export class RenderErrorInsufficientPermissions extends Error {
  constructor(options?: ErrorOptions) {
    super('Application failed to render because of insufficient permissions (HTTP 401 or 403)', options);

    // Maintains proper stack trace for where our error was thrown (non-standard)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, RenderErrorInsufficientPermissions);
    }

    this.name = 'RenderErrorInsufficientPermissions';
  }
}

export class RenderErrorNotFound extends Error {
  constructor(options?: ErrorOptions) {
    super('Application failed to render because the requested resource was not found (HTTP 404)', options);

    // Maintains proper stack trace for where our error was thrown (non-standard)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, RenderErrorNotFound);
    }

    this.name = 'RenderErrorNotFound';
  }
}

export class RenderErrorServerException extends Error {
  constructor(options?: ErrorOptions) {
    super('Application failed to render because of an unexpected server error (HTTP 500)', options);

    // Maintains proper stack trace for where our error was thrown (non-standard)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, RenderErrorServerException);
    }

    this.name = 'RenderErrorServerException';
  }
}

export class RenderErrorSomethingWrong extends Error {
  constructor(options?: ErrorOptions) {
    super('Application failed to render for unspecified reason', options);

    // Maintains proper stack trace for where our error was thrown (non-standard)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, RenderErrorSomethingWrong);
    }

    this.name = 'RenderErrorSomethingWrong';
  }
}
